import { SensorStatus } from "@sitescene/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { Form } from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Select } from "@/components/ui/select/select";
import { GeneralModal } from "@/features/modals/general-modal";

import type { ShadowLocation, ShadowZone } from "../../types/location-map";

import { useMapCoordinateInputs } from "../../hooks/useMapCoordinateInputs";
import { useCreateSensor } from "../../hooks/useMapEntityCreates";
import { useNameBackedExternalIdSync } from "../../hooks/useNameBackedExternalId";
import { createSensorMarkerFeature } from "../../map/markers/create-marker-feature";
import { getRefId } from "../../utils/location/installation-ids";
import { buildLocationMapOverlays } from "../../utils/location/location-map-features";
import { isValidCoordinatePair } from "../../utils/shared/coordinates";
import { hasDuplicateExternalId } from "../../utils/shared/external-id";
import { CoordinateFields } from "./shared/coordinate-fields";
import { createAtLocationDescription } from "./shared/create-at-location-description";
import { MapPlacementSection } from "./shared/map-placement-section";

const SENSOR_TYPE_OPTIONS = [
	{ id: "camera", label: "Camera" },
	{ id: "radar", label: "Radar" },
	{ id: "door", label: "Door" },
	{ id: "air_quality", label: "Air Quality" },
	{ id: "motion", label: "Motion" },
];

const SENSOR_STATUS_OPTIONS = [
	{ id: SensorStatus.Active, label: "Active" },
	{ id: SensorStatus.Warning, label: "Warning" },
	{ id: SensorStatus.Offline, label: "Offline" },
];

const DUPLICATE_EXTERNAL_ID_MESSAGE =
	"A sensor with this External ID already exists for this installation.";

export type CreateSensorFormProps = {
	defaultInstallationId?: string;
	defaultZoneId?: string;
	location: ShadowLocation;
	mapCenter?: [number, number] | null;
	onClose: () => void;
	onCreated?: (sensorId: string) => void;
};

type CreateSensorFormValues = {
	externalId: string;
	latitude: string;
	longitude: string;
	name: string;
	status: string;
	type: string;
	zone: string;
};

export const CreateSensorForm = ({
	defaultInstallationId,
	defaultZoneId,
	location,
	mapCenter,
	onClose,
	onCreated,
}: CreateSensorFormProps) => {
	const createSensor = useCreateSensor();
	const [submitError, setSubmitError] = useState<null | string>(null);

	const zones = useMemo(() => location.zones ?? [], [location.zones]);
	const sensors = useMemo(
		() => zones.flatMap((zone) => zone.sensors ?? []),
		[zones],
	);

	const zoneItems = useMemo(
		() => zones.map((zone) => ({ id: zone.id, label: zone.name })),
		[zones],
	);

	const defaultValues = useMemo(
		(): CreateSensorFormValues => ({
			externalId: "",
			latitude: "",
			longitude: "",
			name: "",
			status: SensorStatus.Active,
			type: "camera",
			zone:
				defaultZoneId && zones.some((zone) => zone.id === defaultZoneId)
					? defaultZoneId
					: (zones[0]?.id ?? ""),
		}),
		[defaultZoneId, zones],
	);

	const {
		control,
		formState: { errors, isValid },
		getValues,
		handleSubmit,
		setError,
		setValue,
		trigger,
	} = useForm<CreateSensorFormValues>({
		defaultValues,
		mode: "onChange",
	});

	const { syncExternalIdChange, syncNameChange } =
		useNameBackedExternalIdSync(setValue);

	const selectedZoneId = useWatch({ control, name: "zone" }) ?? "";
	const watchedLatitude = useWatch({ control, name: "latitude" }) ?? "";
	const watchedLongitude = useWatch({ control, name: "longitude" }) ?? "";
	const watchedName = useWatch({ control, name: "name" }) ?? "";
	const watchedType = useWatch({ control, name: "type" }) ?? "camera";
	const watchedStatus =
		useWatch({ control, name: "status" }) ?? SensorStatus.Active;

	const selectedZone: ShadowZone | undefined = zones.find(
		(zone) => zone.id === selectedZoneId,
	);
	const installationId =
		getRefId(selectedZone?.installation) || defaultInstallationId || "";

	const {
		hasPartialCoordinates,
		mapFrameCenter,
		onMoveEnd,
		parsedCoordinates,
		viewportCenter,
	} = useMapCoordinateInputs({
		fallbackCenter: location.coordinates?.coordinates,
		latitude: watchedLatitude,
		longitude: watchedLongitude,
		mapCenter,
	});

	// Re-check externalId when the zone (and thus installation) changes.
	useEffect(() => {
		if (!getValues("externalId").trim()) return;
		void trigger("externalId");
	}, [getValues, selectedZoneId, trigger]);

	const zoneSensorMarkers = useMemo((): GeoJSON.Feature[] => {
		if (!selectedZoneId) return [];
		return (selectedZone?.sensors ?? [])
			.filter((sensor) =>
				isValidCoordinatePair(sensor.geolocation?.coordinates ?? undefined),
			)
			.map((sensor) =>
				createSensorMarkerFeature({
					coordinates: sensor.geolocation!.coordinates as [number, number],
					dimmed: true,
					id: sensor.id,
					lastInputReceived: sensor.lastInputReceived,
					locationId: location.id,
					name: sensor.name,
					status: sensor.status,
					type: sensor.type,
					zoneId: selectedZoneId,
				}),
			);
	}, [location.id, selectedZone?.sensors, selectedZoneId]);

	const newSensorMarker = useMemo((): GeoJSON.Feature | undefined => {
		if (!parsedCoordinates) return undefined;
		const feature = createSensorMarkerFeature({
			coordinates: parsedCoordinates,
			id: "new-sensor",
			locationId: location.id,
			name: watchedName.trim() || "New Sensor",
			status: (watchedStatus as SensorStatus) || SensorStatus.Active,
			type: watchedType || "camera",
			zoneId: selectedZoneId || undefined,
		});
		return {
			...feature,
			properties: {
				...feature.properties,
				dimmed: false,
				editable: true,
			},
		};
	}, [
		location.id,
		parsedCoordinates,
		selectedZoneId,
		watchedName,
		watchedStatus,
		watchedType,
	]);

	const mapMarkers = useMemo((): GeoJSON.Feature[] | undefined => {
		const markers = [...zoneSensorMarkers];
		if (newSensorMarker) markers.push(newSensorMarker);
		return markers.length > 0 ? markers : undefined;
	}, [newSensorMarker, zoneSensorMarkers]);

	const mapOverlays = useMemo(() => {
		if (!selectedZoneId) return undefined;
		const excludeZoneIds = new Set(
			zones.map((zone) => zone.id).filter((id) => id !== selectedZoneId),
		);
		return buildLocationMapOverlays(location, { excludeZoneIds });
	}, [location, selectedZoneId, zones]);

	const setCoordinatePair = useCallback(
		([lng, lat]: [number, number]) => {
			setValue("latitude", String(lat), {
				shouldDirty: true,
				shouldValidate: true,
			});
			setValue("longitude", String(lng), {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[setValue],
	);

	const handleMapClick = useCallback(
		(coordinates: [number, number]) => {
			setCoordinatePair(coordinates);
		},
		[setCoordinatePair],
	);

	const handleMarkerDragEnd = useCallback(
		(_feature: GeoJSON.Feature, coordinates: [number, number]) => {
			setCoordinatePair(coordinates);
		},
		[setCoordinatePair],
	);

	const handleUseMapCenter = useCallback(() => {
		if (!viewportCenter) return;
		setCoordinatePair(viewportCenter);
	}, [setCoordinatePair, viewportCenter]);

	const validateExternalId = (value: string): string | true => {
		const trimmed = value.trim();
		if (!trimmed) return true;
		const zoneId = getValues("zone");
		const zone = zones.find((item) => item.id === zoneId);
		const installation =
			getRefId(zone?.installation) || defaultInstallationId || undefined;
		if (hasDuplicateExternalId(trimmed, installation, sensors)) {
			return DUPLICATE_EXTERNAL_ID_MESSAGE;
		}
		return true;
	};

	const canSubmit =
		isValid &&
		zoneItems.length > 0 &&
		installationId.length > 0 &&
		!hasPartialCoordinates &&
		!createSensor.isPending;

	const onSubmit = handleSubmit((data) => {
		const zone = zones.find((item) => item.id === data.zone);
		if (!zone) {
			setError("zone", { message: "Zone is required" });
			return;
		}

		const installation =
			getRefId(zone.installation) || defaultInstallationId || "";
		if (!installation) {
			setError("zone", {
				message: "Selected zone is missing an installation",
			});
			return;
		}

		if (hasDuplicateExternalId(data.externalId, installation, sensors)) {
			setError("externalId", { message: DUPLICATE_EXTERNAL_ID_MESSAGE });
			return;
		}

		const lat = parseFloat(data.latitude);
		const lng = parseFloat(data.longitude);
		const coordinates = isValidCoordinatePair([lng, lat])
			? ([lng, lat] as [number, number])
			: undefined;
		const hasPartial =
			(data.latitude.trim() !== "" || data.longitude.trim() !== "") &&
			!coordinates;

		if (hasPartial) {
			setError("latitude", {
				message: "Enter both latitude and longitude to map this sensor.",
			});
			setError("longitude", {
				message: "Enter both latitude and longitude to map this sensor.",
			});
			return;
		}

		setSubmitError(null);
		createSensor.mutate(
			{
				externalId: data.externalId.trim(),
				geolocation: coordinates ? { coordinates, type: "Point" } : undefined,
				installation,
				name: data.name.trim(),
				status: data.status as SensorStatus,
				type: data.type || "camera",
				zone: data.zone,
			},
			{
				onError: () => {
					setSubmitError("Could not create sensor.");
				},
				onSuccess: (created) => {
					onCreated?.(created.id);
					onClose();
				},
			},
		);
	});

	return (
		<GeneralModal
			closeOnSubmit={false}
			description={createAtLocationDescription(location.name, {
				fallback: "Used in alerts and location maps.",
				withLocation: (locationLabel) => <>Add a sensor for {locationLabel}.</>,
			})}
			isOpen
			onClose={onClose}
			onSubmit={onSubmit}
			panelClassName="sm:max-w-160"
			submitDisabled={!canSubmit}
			submitText={createSensor.isPending ? "Creating…" : "Create sensor"}
			title="Create sensor"
		>
			<Form
				className="flex flex-col gap-4 pb-2"
				onSubmit={onSubmit}
				validationBehavior="aria"
			>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<Input
							errorMessage={errors.name?.message}
							isInvalid={Boolean(errors.name)}
							isRequired
							label="Name"
							onBlur={field.onBlur}
							onChange={(value) => syncNameChange(value, field.onChange)}
							placeholder="Front door cam"
							ref={field.ref}
							value={field.value}
						/>
					)}
					rules={{
						required: "Sensor name is required",
						validate: (value) =>
							value.trim().length > 0 || "Sensor name is required",
					}}
				/>
				<Controller
					control={control}
					name="externalId"
					render={({ field }) => (
						<Input
							errorMessage={errors.externalId?.message}
							hint="Auto-filled from the name; edit anytime."
							isInvalid={Boolean(errors.externalId)}
							isRequired
							label="External ID"
							onBlur={field.onBlur}
							onChange={(value) => {
								syncExternalIdChange(value, field.onChange);
								void trigger("externalId");
							}}
							placeholder="front-door-cam"
							ref={field.ref}
							value={field.value}
						/>
					)}
					rules={{
						required: "External ID is required",
						validate: validateExternalId,
					}}
				/>
				<Controller
					control={control}
					name="type"
					render={({ field }) => (
						<Select
							items={[...SENSOR_TYPE_OPTIONS]}
							label="Type"
							onChange={(value) => {
								field.onChange(value ? String(value) : "camera");
							}}
							value={field.value}
						>
							{(item) => (
								<Select.Item id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}
				/>
				<Controller
					control={control}
					name="status"
					render={({ field }) => (
						<Select
							items={[...SENSOR_STATUS_OPTIONS]}
							label="Status"
							onChange={(value) => {
								field.onChange(value ? String(value) : SensorStatus.Active);
							}}
							value={field.value}
						>
							{(item) => (
								<Select.Item id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}
				/>
				<Controller
					control={control}
					name="zone"
					render={({ field }) => (
						<Select
							errorMessage={errors.zone?.message}
							isDisabled={zoneItems.length === 0}
							isInvalid={Boolean(errors.zone)}
							isRequired
							items={zoneItems}
							label="Zone"
							onChange={(value) => {
								field.onChange(value ? String(value) : "");
							}}
							placeholder={
								zoneItems.length === 0 ? "No zones available" : "Select zone"
							}
							value={field.value || null}
						>
							{(item) => (
								<Select.Item id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}
					rules={{ required: "Zone is required" }}
				/>

				<hr className="my-1 border-secondary" />

				<div className="flex flex-col gap-2">
					<h3 className="text-sm font-semibold text-primary">
						Map Coordinates
					</h3>
					<p className="text-sm text-tertiary">
						Optional. Click the map or drag the marker to place the sensor, or
						enter latitude and longitude manually. Other sensors in this zone
						appear dimmed for reference.
					</p>
				</div>

				<CoordinateFields control={control} errors={errors} />

				<MapPlacementSection
					center={mapFrameCenter}
					fitBoundsKey={selectedZoneId || undefined}
					hasPartialCoordinates={hasPartialCoordinates}
					markers={mapMarkers}
					markersInteractive={Boolean(newSensorMarker)}
					onMapClick={handleMapClick}
					onMarkerDragEnd={newSensorMarker ? handleMarkerDragEnd : undefined}
					onMoveEnd={onMoveEnd}
					onUseMapCenter={handleUseMapCenter}
					overlays={mapOverlays}
					partialCoordinatesMessage="Enter both latitude and longitude to map this sensor."
					showUseMapCenter={Boolean(viewportCenter)}
				/>

				{submitError ? (
					<p className="text-sm text-error-primary">{submitError}</p>
				) : null}
			</Form>
		</GeneralModal>
	);
};
